import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { Alert, Heading, Stack } from "@seneca/senekit";

import { Dispatch, GetState } from "common/types";

import { PopupClosedByUser } from "services/firebase/auth/errors";
import errorTranslator from "services/firebase/auth/errorTranslator";
import {
  getNewPrimaryProvider,
  getProviderDisplayName,
  getProviderName,
  getUserDefaultProviders,
  getUserLinkedProviders,
  isOnlyLoginProvider,
  linkProvider,
  shouldUpdatePrimaryProvider,
  unlinkProvider
} from "services/firebase/auth/ssoProvider";
import { ProviderSSO } from "services/firebase/auth/types";

import { getUserEmail } from "seneca-common/features/user/state";
import {
  analyticsLogConnectSSOProvider,
  analyticsLogDisconnectSSOProvider
} from "seneca-common/utils/senecaAnalytics";
import logError from "seneca-common/utils/sentry/logError";

import { updateAttributes } from "features/user";
import { changeDefaultAuthProvider } from "features/user/actions/email";

import SSOProviderRow from "./SSOProviderRow";

const userDefaultProviders = getUserDefaultProviders();

export default function SSOConnections() {
  const [selectedProvider, setSelectedProvider] = useState(undefined);
  const [userProviders, setUserProviders] = useState(userDefaultProviders);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    getProviders(); // eslint-disable-next-line
  }, []);

  function getProviders() {
    setIsLoading(true);

    const linkedProviders = getUserLinkedProviders();

    const userProviders = userDefaultProviders.map(provider => {
      const linkedProvider = linkedProviders.find(
        ({ providerId }) => providerId === provider.providerId
      );

      return linkedProvider
        ? {
            ...linkedProvider,
            disableUnlink: isOnlyLoginProvider(linkedProviders)
          }
        : provider;
    });

    setUserProviders(userProviders);
    setIsLoading(false);
  }

  async function handleConnectionChange(providerId: any, isLinked: any) {
    setError(null);
    setSelectedProvider(providerId);

    try {
      const providerName = getProviderName(providerId);
      const providerDisplayName = getProviderDisplayName(providerId);

      if (isLinked) {
        await unlinkProvider(providerName);
        await dispatch(changeEmailIfNecessary());
        analyticsLogDisconnectSSOProvider(providerDisplayName as ProviderSSO);
      } else {
        await linkProvider(providerName);
        analyticsLogConnectSSOProvider(providerDisplayName as ProviderSSO);
      }

      await dispatch(
        updateAttributes({
          providerData: getUserLinkedProviders()
        })
      );

      setSelectedProvider(undefined);
      getProviders();
    } catch (error: any) {
      setSelectedProvider(undefined);

      switch (error.code) {
        case "auth/popup-closed-by-user":
          const errorPopupClosedByUser = errorTranslator(
            new PopupClosedByUser()
          );
          setError(errorPopupClosedByUser.message);
          break;
        case "auth/email-already-in-use":
        case "auth/credential-already-in-use":
          setError(
            "This credential is already associated with a different user account."
          );
          break;
        default:
          logError(error, { message: "Error handling SSO provider change" });
          setError("Sorry, something went wrong.");
      }
    }
  }

  return (
    <Stack space="xlarge">
      <Heading level="3">Login methods</Heading>

      {userProviders.map((provider, idx) => {
        const loading = isLoading || selectedProvider === provider.providerId;
        const disabled =
          (provider as any).disableUnlink ||
          (!!selectedProvider && selectedProvider !== provider.providerId);

        return (
          <SSOProviderRow
            provider={provider}
            handleConnectionChange={handleConnectionChange}
            loading={loading}
            disabled={disabled}
            idx={idx}
            key={idx}
          />
        );
      })}

      {error && (
        <Alert iconName="exclamationCircle" tone="error">
          {error}
        </Alert>
      )}
    </Stack>
  );
}

function changeEmailIfNecessary() {
  return async function (dispatch: Dispatch, getState: GetState) {
    const primaryEmail = getUserEmail(getState());

    if (shouldUpdatePrimaryProvider(primaryEmail!)) {
      const { email, providerId } = getNewPrimaryProvider();
      const providerName = getProviderName(providerId);
      await dispatch(changeDefaultAuthProvider(email, providerName));
    }
  };
}
